import type { FilterPayloadNode } from '@zoryn/components/grid'

export type RuleMatchContext = Record<string, unknown>

export interface RuleMatchBinding<T = Record<string, unknown>> {
  id?: number | string
  ruleName?: string
  priority?: number
  enabled?: boolean
  rulesJson: FilterPayloadNode | FilterPayloadNode[] | null
  resultJson: T
  ruleScript?: string
}

export interface RuleMatchResult<T = Record<string, unknown>> {
  matched: boolean
  binding?: RuleMatchBinding<T>
  message?: string
}

function isEmptyValue(val: unknown) {
  return val === null || val === undefined || val === ''
}

function compareValues(actual: unknown, expect: unknown) {
  const left = Number(actual)
  const right = Number(expect)
  if (!Number.isNaN(left) && !Number.isNaN(right)) {
    return left - right
  }
  return String(actual ?? '').localeCompare(String(expect ?? ''))
}

function like(actual: string, pattern: string) {
  if (!pattern) return true
  return actual.toLowerCase().includes(String(pattern).toLowerCase())
}

function evaluateCondition(node: FilterPayloadNode, context: RuleMatchContext) {
  const field = node.field!
  const actual = context[field]
  const expect = node.value
  const op = node.operator ?? 'eq'

  switch (op) {
    case 'eq':
    case '=':
      if (typeof expect === 'boolean') {
        return Boolean(actual) === expect
      }
      return String(actual ?? '') === String(expect ?? '')
    case 'ne':
      return String(actual ?? '') !== String(expect ?? '')
    case 'like':
      return like(String(actual ?? ''), String(expect ?? ''))
    case 'notLike':
      return !like(String(actual ?? ''), String(expect ?? ''))
    case 'gt':
      return compareValues(actual, expect) > 0
    case 'ge':
      return compareValues(actual, expect) >= 0
    case 'lt':
      return compareValues(actual, expect) < 0
    case 'le':
      return compareValues(actual, expect) <= 0
    case 'empty':
      return isEmptyValue(actual)
    case 'notEmpty':
      return !isEmptyValue(actual)
    default:
      return false
  }
}

function evaluateNode(node: FilterPayloadNode, context: RuleMatchContext): boolean {
  if (node.type === 'group' && node.children?.length) {
    return evaluateGroup(node.children, context)
  }
  if (node.field) {
    return evaluateCondition(node, context)
  }
  if (node.children?.length) {
    return evaluateGroup(node.children, context)
  }
  return true
}

function evaluateGroup(nodes: FilterPayloadNode[], context: RuleMatchContext) {
  if (!nodes.length) return true
  let result = evaluateNode(nodes[0], context)
  for (let i = 1; i < nodes.length; i += 1) {
    const next = evaluateNode(nodes[i], context)
    result = nodes[i].logic === 'or' ? result || next : result && next
  }
  return result
}

function normalizeRules(rulesJson: FilterPayloadNode | FilterPayloadNode[] | null) {
  if (rulesJson == null) {
    return null
  }
  if (Array.isArray(rulesJson)) {
    return rulesJson
  }
  return [rulesJson]
}

export function evaluateRulesJson(
  rulesJson: FilterPayloadNode | FilterPayloadNode[] | null,
  context: RuleMatchContext
): boolean {
  const rules = normalizeRules(rulesJson)
  if (rules == null) {
    return true
  }
  return evaluateGroup(rules, context)
}

export function matchRuleBindings<T extends Record<string, unknown>>(
  bindings: RuleMatchBinding<T>[],
  context: RuleMatchContext,
  options?: {
    filter?: (binding: RuleMatchBinding<T>) => boolean
  }
): RuleMatchResult<T> {
  const candidates = bindings
    .filter((item) => item.enabled !== false)
    .filter((item) => (options?.filter ? options.filter(item) : true))
    .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))

  if (!candidates.length) {
    return { matched: false, message: '未配置可用规则' }
  }

  for (const binding of candidates) {
    if (evaluateRulesJson(binding.rulesJson, context)) {
      return { matched: true, binding }
    }
  }

  return { matched: false, message: '未命中任何规则' }
}
